import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CleaningSettings } from "@/components/CleaningSettings";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { clearConfig } from "@/lib/config";

export default function OwnerSettings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/owner/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const setArea = (i, k, v) => setS({ ...s, areas: s.areas.map((a, j) => (j === i ? { ...a, [k]: v } : a)) });
  const save = async () => {
    try { const r = await api.put("/owner/settings", s); setS(r.data); clearConfig(); toast.success(t("o.settings_saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="owner-settings">
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <p className="font-semibold mb-4">{t("o.active_cats", { n: SERVICES.length })}</p>
        <div className="max-h-[520px] overflow-y-auto pe-2">
        {SERVICES.map(({ key }) => (
          <label key={key} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">{t(`svc.${key}`)}
            <Switch checked={!!s.categories[key]} onCheckedChange={(v) => setS({ ...s, categories: { ...s.categories, [key]: v } })} data-testid={`setting-cat-${key}`} />
          </label>
        ))}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <p className="font-semibold mb-1">{t("o.areas")}</p>
        <p className="text-xs text-slate-500 mb-4">{t("o.areas_d")}</p>
        <div className="space-y-2">
          {s.areas.map((a, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`setting-area-${i}`}>
              <Input value={a.name} onChange={(e) => setArea(i, "name", e.target.value)} className="h-9" data-testid={`setting-area-name-${i}`} />
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">{t("o.active")}<Switch checked={a.active} onCheckedChange={(v) => setArea(i, "active", v)} data-testid={`setting-area-active-${i}`} /></label>
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">{t("o.goal24")}<Switch checked={a.support_24h} onCheckedChange={(v) => setArea(i, "support_24h", v)} data-testid={`setting-area-24h-${i}`} /></label>
              <label className="flex items-center gap-1 text-xs whitespace-nowrap">24/7<Switch checked={!!a.support_247} onCheckedChange={(v) => setArea(i, "support_247", v)} data-testid={`setting-area-247-${i}`} /></label>
              <button onClick={() => setS({ ...s, areas: s.areas.filter((_, j) => j !== i) })} className="p-1 text-slate-400 hover:text-red-600" data-testid={`setting-area-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => setS({ ...s, areas: [...s.areas, { name: "", active: true, support_24h: false, support_247: false }] })} data-testid="setting-add-area"><Plus className="h-4 w-4 me-1" />{t("o.add_area")}</Button>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:col-span-2">
        <p className="font-semibold mb-1">{t("o.wa_title")}</p>
        <p className="text-xs text-slate-500 mb-3">{t("o.wa_d")}</p>
        <Input value={s.whatsapp || ""} onChange={(e) => setS({ ...s, whatsapp: e.target.value })} placeholder="50588881234" className="max-w-xs" data-testid="setting-whatsapp-input" />
      </div>
      <CleaningSettings cleaning={s.cleaning} setCleaning={(fn) => setS((p) => ({ ...p, cleaning: fn(p.cleaning) }))} />
      <Button className="lg:col-span-2 justify-self-start rounded-full bg-teal-600 hover:bg-teal-700 px-8" onClick={save} data-testid="settings-save-btn">{t("o.save_settings")}</Button>
    </div>
  );
}
